import { onDocumentReady } from "@story/story_assets/utils";

//
// One item in filter (service or article)
//
function FilterItem(element, type, category, serviceId = null) {
	this.element = element;
	this.type = type; // 'category', 'service', 'article'
	this.category = category;
	this.serviceId = serviceId;
}

FilterItem.prototype = {
	constructor: FilterItem,

	show: function() {
		this.element.style.display = '';
	},

	hide: function() {
		this.element.style.display = 'none';
	},

	activate: function() {
		this.element.classList.add('active');
	},

	deactivate: function() {
		this.element.classList.remove('active');
	},

	isVisible: function() {
		return this.element.style.display !== 'none';
	},

	isActive: function() {
		return this.element.classList.contains('active');
	},

	matchesCategory: function(category) {
		return !category || this.category === category;
	},

	matchesService: function(serviceId) {
		return !serviceId || this.serviceId === serviceId;
	}
};

//
// Main class for filtering services
//
export function ServiceFilter(wrapper) {
	this.wrapper = wrapper;
	this.currentCategory = null;
	this.currentServiceId = null;

	// Containers
	this.servicesSection = document.querySelector('.section-services .row');
	this.categoriesContainer = wrapper.querySelector('[data-filter="categories"]');
	this.servicesContainer = wrapper.querySelector('[data-filter="services"]');
	this.articlesContainer = wrapper.querySelector('[data-filter="articles"]');

	// Initialize items
	this.initializeItems();
	this.bindEvents();
	this.initializeState();
}

ServiceFilter.prototype = {
	constructor: ServiceFilter,

	//
	// Initialize all filter items
	//
	initializeItems: function() {
		this.categories = [];
		this.services = [];
		this.articles = [];
		this.serviceCards = [];

		// Categories
		const categoryElements = document.querySelectorAll('[data-category-filter]');
		for (let element of categoryElements) {
			const category = element.getAttribute('data-category-filter');
			this.categories.push(new FilterItem(element, 'category', category));
		}

		// Services in navigation
		const serviceElements = document.querySelectorAll('[data-service-category]');
		for (let element of serviceElements) {
			const category = element.getAttribute('data-service-category');
			const serviceId = element.getAttribute('data-service-id');
			this.services.push(new FilterItem(element, 'service', category, serviceId));
		}

		// Articles
		const articleElements = document.querySelectorAll('[data-article-service-category]');
		for (let element of articleElements) {
			const category = element.getAttribute('data-article-service-category');
			const serviceId = element.getAttribute('data-article-service-id');
			this.articles.push(new FilterItem(element, 'article', category, serviceId));
		}

		// Service cards in main section
		if (this.servicesSection) {
			const cardElements = this.servicesSection.querySelectorAll('[data-category]');
			for (let element of cardElements) {
				const category = element.getAttribute('data-category');
				this.serviceCards.push(new FilterItem(element, 'card', category));
			}
		}
	},

	//
	// Bind event handlers
	//
	bindEvents: function() {
		// Click on categories
		for (let categoryItem of this.categories) {
			categoryItem.element.addEventListener('click', this.onCategoryClick.bind(this));
		}

		// Click on services
		for (let serviceItem of this.services) {
			serviceItem.element.addEventListener('click', this.onServiceClick.bind(this));
		}
	},

	//
	// Handler click on category
	//
	onCategoryClick: function(event) {
		event.preventDefault();
		const category = event.currentTarget.getAttribute('data-category-filter');
		this.filterByCategory(category);
	},

	//
	// Handler click on service
	//
	onServiceClick: function(event) {
		event.preventDefault();
		const serviceId = event.currentTarget.getAttribute('data-service-id');
		this.filterByService(serviceId);
	},

	//
	// Filter by category
	//
	filterByCategory: function(category) {
		this.currentCategory = category;
		this.currentServiceId = null; // Reset service selection

		// Filter all item types
		this.filterItems(this.serviceCards, category, null);
		this.filterItems(this.services, category, null);
		this.filterItems(this.articles, category, null);

		// Update active states
		this.updateActiveStates();

		// Auto-select first visible service
		this.autoSelectFirstService();

		// Trigger custom event
		this.triggerEvent('categoryChanged', { category: category });
	},

	//
	// Filter by service
	//
	filterByService: function(serviceId) {
		this.currentServiceId = serviceId;

		// Filter only articles by service (keep category)
		this.filterItems(this.articles, this.currentCategory, serviceId);

		// Update active states
		this.updateActiveStates();

		// Auto-select first visible article
		this.autoSelectFirstArticle();

		// Trigger custom event
		this.triggerEvent('serviceChanged', { serviceId: serviceId });
	},

	//
	// General method for filtering items
	//
	filterItems: function(items, category, serviceId) {
		for (let item of items) {
			const matchesCategory = item.matchesCategory(category);
			const matchesService = item.matchesService(serviceId);

			if (matchesCategory && matchesService) {
				item.show();
			} else {
				item.hide();
			}
		}
	},

	//
	// Update active states
	//
	updateActiveStates: function() {
		// Deactivate all
		this.deactivateAll();

		// Activate current selection
		this.activateByCategory(this.currentCategory);
		this.activateByService(this.currentServiceId);
	},

	//
	// Deactivate all items
	//
	deactivateAll: function() {
		const allItems = [...this.categories, ...this.services, ...this.articles];
		for (let item of allItems) {
			item.deactivate();
		}
	},

	//
	// Activate by category
	//
	activateByCategory: function(category) {
		for (let categoryItem of this.categories) {
			if (categoryItem.category === category) {
				categoryItem.activate();
				break;
			}
		}
	},

	//
	// Activate by service
	//
	activateByService: function(serviceId) {
		for (let serviceItem of this.services) {
			if (serviceItem.serviceId === serviceId) {
				serviceItem.activate();
				break;
			}
		}
	},

	//
	// Auto-select first visible service
	//
	autoSelectFirstService: function() {
		const firstVisible = this.services.find(item => item.isVisible());
		if (firstVisible) {
			this.filterByService(firstVisible.serviceId);
		}
	},

	//
	// Auto-select first visible article
	//
	autoSelectFirstArticle: function() {
		const firstVisible = this.articles.find(item => item.isVisible());
		if (firstVisible) {
			firstVisible.activate();
		}
	},

	//
	// Initialize default state
	//
	initializeState: function() {
		if (this.categories.length > 0) {
			const firstCategory = this.categories[0].category;
			this.filterByCategory(firstCategory);
		}
	},

	//
	// Trigger custom event
	//
	triggerEvent: function(eventName, detail) {
		const event = new CustomEvent(eventName, {
			detail: detail,
			bubbles: true
		});
		this.wrapper.dispatchEvent(event);
	},

	//
	// Public API for external control
	//
	setCategory: function(category) {
		this.filterByCategory(category);
	},

	setService: function(serviceId) {
		this.filterByService(serviceId);
	},

	getCurrentCategory: function() {
		return this.currentCategory;
	},

	getCurrentService: function() {
		return this.currentServiceId;
	}
};

//
// Initialize when page is loaded
//
onDocumentReady(function() {
	const filterWrapper = document.querySelector('.section-body');
	if (filterWrapper) {
		window.serviceFilter = new ServiceFilter(filterWrapper);
	}
});
